import {
  type CoreListing,
  useGetListingsMostRecentQuery,
  usePopularListingsQuery,
} from '@kijiji/generated/graphql-types'
import { colors, spacing } from '@kijiji/theme'
import { useTranslation } from 'next-i18next'
import { type FC, useMemo } from 'react'

import { CoreListingCard } from '@/components/shared/core-listing-card/CoreListingCard'
import { ErrorBoundary } from '@/components/shared/error-boundary'
import { LinkCustom } from '@/components/shared/link-custom/LinkCustom'
import { ListingsCarousel } from '@/components/shared/listings-carousel/ListingsCarousel'
import { isValidLocation } from '@/domain/location/isValidLocation'
import { MOST_RECENT_LISTINGS_TO_FETCH } from '@/features/listing/components/galleries/most-recent-listings/constants/constants'
import { getSeeAllLink } from '@/features/listing/components/galleries/most-recent-listings/utils/getSeeAllLink'
import { getPopularListingsVariables } from '@/features/listing/components/galleries/popular-listings/getListingsPopular'
import {
  PopularListingsHeaderWrapper,
  PopularListingsWrapper,
} from '@/features/listing/components/galleries/popular-listings/styled'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { HeadlineText } from '@/ui/atoms/headline-text'
import { Spacing } from '@/ui/atoms/spacing'
/**
 * Props for the PopularListings component.
 *
 * @param locationId - The ID of the location to filter popular listings.
 * @param categoryId - The ID of the category to filter popular listings.
 */
export type PopularListingsProps = {
  locationId: number
  categoryId: number
  sectionKey: 'autos' | 'real_estate' | 'buy_sell'
  locationName?: string | null
}

export const PopularListings: FC<PopularListingsProps> = ({
  locationId,
  categoryId,
  sectionKey,
  locationName,
}) => {
  const { t } = useTranslation('home')

  const initialFetchVariables = useMemo(
    () => getPopularListingsVariables(locationId, categoryId),
    [locationId, categoryId]
  )

  const { loading, data } = usePopularListingsQuery({
    skip: !isValidLocation(locationId),
    variables: initialFetchVariables,
  })

  const { data: listingsMostRecent } = useGetListingsMostRecentQuery({
    fetchPolicy: 'cache-first',
    variables: {
      by: {
        query: {
          oneListingPerUser: true,
          categoryId,
          location: {
            id: locationId,
          },
        },
      },
      pagination: {
        limit: MOST_RECENT_LISTINGS_TO_FETCH,
      },
    },
  })

  const handleImpressionsTracking = (listingId: string, position: number) => {
    trackEvent({
      action: GA_EVENT.SelectPromotion,
      label: `partneradid=${listingId};position=${position};promotion_name=popular_listings`,
    })
  }

  const slides =
    data?.listingsPopular?.listings?.map((item, index) => {
      if (!item) return null

      const position = index + 1
      return (
        <CoreListingCard
          key={`popular-listings-${item.id}`}
          index={index}
          listing={item}
          linkTarget="_self"
          onClick={() => handleImpressionsTracking(item.id, position)}
        />
      )
    }) || []

  if (!loading && !data?.listingsPopular?.listings?.length) return null

  const seeAllLink = getSeeAllLink(
    listingsMostRecent?.searchResultsPage.searchQuery.seoUrl,
    locationId,
    locationName,
    t
  )

  return (
    <ErrorBoundary fingerprintId="PopularListings" hideError hideHelpFixIssue>
      <PopularListingsWrapper data-testid="popular-listings-section">
        <Spacing mBottom={spacing.default}>
          <PopularListingsHeaderWrapper justifyContent="space-between" alignItems="center">
            <HeadlineText as="h3" color={colors.grey.primary} size="medium" weight="regular">
              {t('popular_categories.carousel.prefix')}{' '}
              {t(`popular_categories.header.${sectionKey}_category`)}
            </HeadlineText>
            <LinkCustom href={seeAllLink} weight="regular" size="small" variant="secondary">
              {t('gallery.see_all.label')}
            </LinkCustom>
          </PopularListingsHeaderWrapper>
        </Spacing>

        <ListingsCarousel
          enableImpressionsTracking
          trackSlidesListings={
            data?.listingsPopular?.listings?.filter((item): item is CoreListing => item !== null) ??
            []
          }
          slides={slides}
          name="popular-listings"
          shouldShowArrows={{ small: false, medium: false, large: true }}
          customShowcaseOptions={{ slidesToScroll: 'auto' }}
          slidesToShow={{
            small: 2,
            medium: 3,
            large: 5,
          }}
        />
      </PopularListingsWrapper>
    </ErrorBoundary>
  )
}
